import { useEffect, useState, useRef } from "react";
import { Upload, Trash2, Pencil, Star, X } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, formatApiErrorDetail, GALLERY_CATEGORIES } from "../lib/api";

const uploadProgress = (e) => { if (e.total) toast.loading(`Uploading… ${Math.round((e.loaded / e.total) * 100)}%`, { id: "upload-progress" }); };
export const MEDIA_ACCEPT = "image/*,.heic,video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm";

const EMPTY = { title: "", category: "Remodeling", description: "", image: "", featured: false, published: true };

export default function GalleryManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const thumbRef = useRef(null);
  const multiRef = useRef(null);

  const load = () => api.get("/admin/gallery").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadFiles = async (files) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/admin/upload", fd, { onUploadProgress: uploadProgress });
      toast.success("Upload complete", { id: "upload-progress" });
      return data.urls;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed", { id: "upload-progress" });
      throw e;
    }
  };

  const multiUpload = async (files) => {
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = await uploadFiles(files);
      for (const u of urls) {
        await api.post("/admin/gallery", { ...EMPTY, title: "New Project Photo", image: u });
      }
      toast.success(`${urls.length} file(s) uploaded`);
      load();
    } catch (e) {}
    setBusy(false);
  };

  const save = async () => {
    if (!editing.image) { toast.error("Please upload a photo"); return; }
    setBusy(true);
    try {
      if (editing.id) await api.put(`/admin/gallery/${editing.id}`, editing);
      else await api.post("/admin/gallery", editing);
      toast.success("Saved — public gallery updated");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this photo from the gallery? This cannot be undone.")) return;
    await api.delete(`/admin/gallery/${id}`);
    toast.success("Photo deleted");
    load();
  };

  const toggleFeature = async (g) => {
    await api.put(`/admin/gallery/${g.id}`, { ...g, featured: !g.featured });
    load();
  };

  return (
    <div data-testid="portal-gallery-manager">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-heading font-extrabold text-[#0B1B3D] text-2xl md:text-3xl">Manage Projects</h1>
        <div className="flex gap-2">
          <button onClick={() => multiRef.current?.click()} disabled={busy} className="btn-outline-dark !py-2.5 !px-4 !text-xs" data-testid="gallery-multi-upload-btn">
            <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Multiple Upload"}
          </button>
          <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary !py-2.5 !px-4 !text-xs" data-testid="gallery-add-btn">Add Photo</button>
        </div>
        <input ref={multiRef} type="file" multiple accept={MEDIA_ACCEPT} className="hidden" onChange={(e) => multiUpload(e.target.files)} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((g) => (
          <div key={g.id} className="bg-white border border-slate-200 overflow-hidden group" data-testid={`gallery-admin-item-${g.id}`}>
            <div className="relative h-40 bg-slate-900">
              {/\.(mp4|mov|webm)$/i.test(g.image || "") ? (
                <video src={imgUrl(g.image)} muted className="w-full h-full object-cover" />
              ) : (
                <img src={`${imgUrl(g.image)}?w=500&q=60`} alt={g.title} className="w-full h-full object-cover" />
              )}
              {g.featured && <span className="absolute top-2 left-2 bg-[#005AE0] text-white text-[10px] font-heading font-bold px-2 py-1 uppercase">Featured</span>}
              {!g.published && <span className="absolute top-2 right-2 bg-slate-800 text-white text-[10px] font-heading font-bold px-2 py-1 uppercase">Hidden</span>}
            </div>
            <div className="p-3">
              <p className="font-heading font-bold text-[#0B1B3D] text-sm truncate">{g.title}</p>
              <p className="text-xs text-slate-400">{g.category}</p>
              <div className="flex gap-1 mt-3">
                <button onClick={() => setEditing(g)} className="flex-1 py-1.5 border border-slate-200 text-slate-500 hover:text-[#005AE0] flex justify-center" data-testid={`gallery-edit-${g.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => toggleFeature(g)} className={`flex-1 py-1.5 border border-slate-200 flex justify-center ${g.featured ? "text-[#005AE0]" : "text-slate-400"}`} data-testid={`gallery-feature-${g.id}`}><Star className="w-4 h-4" /></button>
                <button onClick={() => remove(g.id)} className="flex-1 py-1.5 border border-slate-200 text-slate-400 hover:text-red-500 flex justify-center" data-testid={`gallery-delete-${g.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="gallery-edit-modal">
          <div className="bg-white w-full max-w-lg p-6 md:p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading font-bold text-[#0B1B3D] text-xl">{editing.id ? "Edit Photo" : "Add Photo"}</h2>
              <button onClick={() => setEditing(null)} data-testid="gallery-modal-close"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            {editing.image ? (
              /\.(mp4|mov|webm)$/i.test(editing.image) ? (
                <video src={imgUrl(editing.image)} controls muted className="w-full h-48 object-cover mb-4 border border-slate-200" />
              ) : (
                <img src={imgUrl(editing.image)} alt="" className="w-full h-48 object-cover mb-4 border border-slate-200" />
              )
            ) : null}
            <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-slate-300 py-6 text-slate-500 text-sm mb-3 hover:border-[#005AE0]" data-testid="gallery-modal-upload">
              {editing.image ? "Replace photo / video" : "Upload photo or video"}
            </button>
            {/\.(mp4|mov|webm)$/i.test(editing.image || "") && (
              <div className="mb-4 flex items-center gap-3">
                {editing.thumbnail && <img src={imgUrl(editing.thumbnail)} alt="thumbnail" className="w-16 h-12 object-cover border border-slate-200" />}
                <button onClick={() => thumbRef.current?.click()} className="btn-outline-dark !py-2 !px-3 !text-[11px]" data-testid="gallery-thumbnail-btn">
                  {editing.thumbnail ? "Replace video thumbnail" : "Upload video thumbnail (optional)"}
                </button>
                <input ref={thumbRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
                  onChange={async (e) => {
                    if (e.target.files[0]) {
                      const urls = await uploadFiles(e.target.files);
                      setEditing({ ...editing, thumbnail: urls[0] });
                    }
                  }} />
              </div>
            )}
            <input ref={fileRef} type="file" accept={MEDIA_ACCEPT} className="hidden"
              onChange={async (e) => {
                if (e.target.files[0]) {
                  const urls = await uploadFiles(e.target.files);
                  setEditing({ ...editing, image: urls[0] });
                }
              }} />
            <label className="field-label">Title</label>
            <input className="field mb-4" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="gallery-title-input" />
            <label className="field-label">Category</label>
            <select className="field mb-4" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="gallery-category-select">
              {GALLERY_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <label className="field-label">Description</label>
            <textarea className="field mb-4" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="gallery-description-input" />
            <div className="flex gap-6 mb-6">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="gallery-featured-checkbox" /> Featured
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="gallery-published-checkbox" /> Published
              </label>
            </div>
            <button onClick={save} disabled={busy} className="btn-primary w-full" data-testid="gallery-save-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
